import type { ConflictingCampaign } from "@/@types/campaign-conflict";

import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";

import { ALL_STORES_CODE } from "../components/edit-campaign/constants";
import { gradeTargetLabel } from "../components/grade/grade-messages";
import { COMMON_TEXTS } from "./messages";

const STORES_PREVIEW_LIMIT = 5;
const LIST_SEPARATOR = ", ";
const PARTS_SEPARATOR = " · ";
const GRADES_SEPARATOR = " / ";

export const CONFLICT_SCOPE_TEXTS = {
  ALL_STORES: COMMON_TEXTS.ALL_STORES,
  SINGLE_STORE: COMMON_TEXTS.STORE_LABEL,
  STORES: (codes: string): string => `Lojas ${codes}`,
  MORE_STORES: (count: number): string => ` +${count}`,
  PERIOD: (start: string, end: string): string => `${start} a ${end}`,
  OVERLAP: (period: string): string => `Em comum: ${period}`,
  CAMPAIGN: (type: string, code: number): string => `${type} #${code}`,
} as const;

const storesListLabel = (stores: number[]): string => {
  if (stores.length === 1) return CONFLICT_SCOPE_TEXTS.SINGLE_STORE(stores[0]);
  const visible: string = stores.slice(0, STORES_PREVIEW_LIMIT).join(LIST_SEPARATOR);
  const hidden: number = stores.length - STORES_PREVIEW_LIMIT;
  const suffix: string = hidden > 0 ? CONFLICT_SCOPE_TEXTS.MORE_STORES(hidden) : "";
  return `${CONFLICT_SCOPE_TEXTS.STORES(visible)}${suffix}`;
};

export const conflictStoresLabel = (campaign: ConflictingCampaign): string | null => {
  if (campaign.allStores) return CONFLICT_SCOPE_TEXTS.ALL_STORES;
  if ((campaign.stores?.length ?? 0) > 0) return storesListLabel(campaign.stores ?? []);
  if (campaign.storeCode !== ALL_STORES_CODE) {
    return CONFLICT_SCOPE_TEXTS.SINGLE_STORE(campaign.storeCode);
  }
  return null;
};

export const conflictPeriodLabel = (campaign: ConflictingCampaign): string => {
  if (campaign.overlapStartDate && campaign.overlapEndDate) {
    return CONFLICT_SCOPE_TEXTS.OVERLAP(
      CONFLICT_SCOPE_TEXTS.PERIOD(
        formatDateToDMY(campaign.overlapStartDate),
        formatDateToDMY(campaign.overlapEndDate),
      ),
    );
  }
  return CONFLICT_SCOPE_TEXTS.PERIOD(
    formatDateToDMY(campaign.startDate),
    formatDateToDMY(campaign.endDate),
  );
};

export const conflictGradesLabel = (campaign: ConflictingCampaign): string | null => {
  if ((campaign.grades?.length ?? 0) === 0) return null;
  return (campaign.grades ?? []).map(gradeTargetLabel).join(GRADES_SEPARATOR);
};

export const describeConflictingCampaign = (campaign: ConflictingCampaign): string =>
  [
    CONFLICT_SCOPE_TEXTS.CAMPAIGN(campaign.type, campaign.code),
    conflictGradesLabel(campaign),
    conflictStoresLabel(campaign),
    conflictPeriodLabel(campaign),
  ]
    .filter((part: string | null): part is string => Boolean(part))
    .join(PARTS_SEPARATOR);
